/**
 * Pure helpers for the agency Tasks tab (DD-049, TD-094): row
 * capabilities, the person filter, the list URL and counts.
 */

import type { DropdownOption } from "@/components/ui/OptionDropdown";
import type { AgencyPerson } from "@/domains/Client/agencyPeople.service";
import { assigneePillStyle } from "./taskAssigneeConfig";
import { TaskCapabilities, capabilitiesFor } from "./taskCapabilities";
import type { AgencyTaskFilters, TaskStatus } from "./types";

/** Person filter value for everyone. Maps to `assignee: null`. */
export const ALL_PEOPLE = "__all__";

/** Person filter and API value for unassigned (Team) tasks. Mirrored by
 * TEAM_ASSIGNEE in backend/domains/tasks/task_repository.py. */
export const TEAM_ASSIGNEE = "team";

/** Admin rows without reorder: positions only mean something within one
 * client and one parent (DD-049). */
export const AGENCY_TASK_CAPABILITIES: TaskCapabilities = {
  ...capabilitiesFor("admin"),
  canReorder: false,
};

/** Tutti, Team, then members before contacts, each person once. Server
 * order holds within each kind. Dots match the task pills. */
export function personFilterOptions(people: AgencyPerson[]): DropdownOption<string>[] {
  const seen = new Set<string>();
  const unique = people.filter((person) => {
    if (seen.has(person.id)) return false;
    seen.add(person.id);
    return true;
  });
  const ordered = [
    ...unique.filter((person) => person.kind === "team"),
    ...unique.filter((person) => person.kind !== "team"),
  ];
  const team = assigneePillStyle(null);
  return [
    { value: ALL_PEOPLE, label: "Tutti" },
    { value: TEAM_ASSIGNEE, label: team.label, dot: team.dot },
    ...ordered.map((person) => {
      const style = assigneePillStyle(person);
      return { value: person.id, label: style.label, dot: style.dot };
    }),
  ];
}

export function agencyTasksPath(filters: AgencyTaskFilters): string {
  const params = new URLSearchParams({ status: filters.status });
  if (filters.assignee) params.set("assignee", filters.assignee);
  return `/api/agency/tasks?${params.toString()}`;
}

/** "3 aperti" on Aperti, "3 fatti" on Fatti. */
export function countLabel(count: number, status: TaskStatus): string {
  if (status === "open") return count === 1 ? "1 aperto" : `${count} aperti`;
  return count === 1 ? "1 fatto" : `${count} fatti`;
}

/** Done tasks are capped at this many, the most recent. Mirrors
 * AGENCY_DONE_TASK_LIMIT in backend/domains/tasks/task_repository.py. */
export const AGENCY_DONE_TASK_LIMIT = 100;

/** The panel summary. A full Fatti list reads as a cap, not a total. */
export function summaryLabel(count: number, status: TaskStatus): string {
  if (status === "done" && count >= AGENCY_DONE_TASK_LIMIT) {
    return `Ultimi ${AGENCY_DONE_TASK_LIMIT} fatti`;
  }
  return countLabel(count, status);
}
